import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentToolService } from '../../agent-tool.service';
import {
  AgentTool,
  CreateAgentToolPayload,
  PatchAgentToolPayload,
  TOOL_METHOD_OPTIONS,
} from '../../agent-tool.models';
import { LiveCallAuthService } from '../../../auth/live-call-auth.service';
import { LiveCallAuthConfig } from '../../../auth/live-call-auth.models';
import { AgentMultiSelectComponent } from '../../../../components/agent-multi-select/agent-multi-select.component';

export type ToolPanelMode = 'create' | 'edit';

/** One editable key/value row of the tool's payload template. */
interface PayloadRow {
  key: string;
  value: string;
}

/**
 * Create or edit one live-call agent TOOL: its endpoint (method + base URL),
 * an optional flat payload template, an optional universal auth entry, and
 * the agent prompts that may call it.
 *
 * Unlike the prompt panel, edit mode needs no fetch — the tools list already
 * carries the full record (`config`, `agents`) for every row, so the form
 * populates straight from the `tool` input.
 */
@Component({
  selector: 'app-tool-form-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, AgentMultiSelectComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './tool-form-panel.component.html',
})
export class ToolFormPanelComponent implements OnInit, OnChanges {
  @Input() mode: ToolPanelMode = 'create';
  @Input() tool: AgentTool | null = null;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private toolService = inject(AgentToolService);
  private authService = inject(LiveCallAuthService);

  submitting = signal(false);
  errorMessage = signal('');

  /** Universal auth catalog for the dropdown. */
  authOptions = signal<LiveCallAuthConfig[]>([]);
  authLoading = signal(false);

  readonly methodOptions = TOOL_METHOD_OPTIONS;

  form = {
    tool_name: '',
    tool_keyname: '',
    method: 'POST',
    base_url: '',
    description: '',
    auth_id: null as number | null,
    is_active: true,
    agent_ids: [] as number[],
  };

  /** Payload template rows, collapsed into `config` on submit. */
  payloadRows: PayloadRow[] = [];

  ngOnInit(): void {
    this.loadAuthOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tool'] || changes['mode']) {
      this.errorMessage.set('');
      if (this.mode === 'edit' && this.tool) {
        this.form = {
          tool_name: this.tool.toolName ?? '',
          tool_keyname: this.tool.toolKeyname ?? '',
          method: this.tool.method ?? 'POST',
          base_url: this.tool.baseUrl ?? '',
          description: this.tool.description ?? '',
          auth_id: this.tool.authId ?? null,
          is_active: this.tool.isActive ?? true,
          agent_ids: (this.tool.agents ?? []).map((agent) => agent.id),
        };
        this.payloadRows = this.rowsFromConfig(this.tool.config);
      } else {
        this.form = {
          tool_name: '',
          tool_keyname: '',
          method: 'POST',
          base_url: '',
          description: '',
          auth_id: null,
          is_active: true,
          agent_ids: [],
        };
        this.payloadRows = [{ key: '', value: '' }];
      }
    }
  }

  addPayloadRow(): void {
    this.payloadRows.push({ key: '', value: '' });
  }

  removePayloadRow(index: number): void {
    this.payloadRows.splice(index, 1);
  }

  onSubmit(): void {
    this.errorMessage.set('');
    if (!this.form.tool_name.trim()) return this.errorMessage.set('Name is required.');
    if (!this.form.base_url.trim()) return this.errorMessage.set('Endpoint is required.');
    if (this.mode === 'create' && !this.form.tool_keyname.trim()) {
      return this.errorMessage.set('Keyname is required.');
    }

    const config = this.collapseConfig();

    this.submitting.set(true);
    const done = {
      next: () => {
        this.submitting.set(false);
        this.saved.emit();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.errorMessage.set(err?.message ?? 'Save failed.');
      },
    };

    if (this.mode === 'create') {
      const payload: CreateAgentToolPayload = {
        tool_name: this.form.tool_name.trim(),
        tool_keyname: this.form.tool_keyname.trim(),
        method: this.form.method,
        base_url: this.form.base_url.trim(),
        config,
        description: this.form.description.trim() || null,
        auth_id: this.form.auth_id,
        is_active: this.form.is_active,
        agent_ids: this.form.agent_ids,
      };
      this.toolService.createTool(payload).subscribe(done);
      return;
    }

    if (!this.tool) {
      this.submitting.set(false);
      return this.errorMessage.set('No tool selected to edit.');
    }
    const payload: PatchAgentToolPayload = {
      tool_name: this.form.tool_name.trim(),
      method: this.form.method,
      base_url: this.form.base_url.trim(),
      config,
      description: this.form.description.trim() || null,
      auth_id: this.form.auth_id,
      is_active: this.form.is_active,
      agent_ids: this.form.agent_ids,
    };
    this.toolService.patchTool(this.tool.id, payload).subscribe(done);
  }

  onClose(): void {
    this.closed.emit();
  }

  /** Flatten the editable rows to a config map; empty keys are skipped, an empty map becomes null. */
  private collapseConfig(): Record<string, string> | null {
    const config: Record<string, string> = {};
    for (const row of this.payloadRows) {
      if (row.key.trim()) config[row.key.trim()] = row.value ?? '';
    }
    return Object.keys(config).length ? config : null;
  }

  private rowsFromConfig(config: Record<string, string> | null): PayloadRow[] {
    const rows = Object.entries(config ?? {}).map(([key, value]) => ({ key, value }));
    return rows.length ? rows : [{ key: '', value: '' }];
  }

  /** Only active entries — a tool should not newly authenticate with a disabled one. */
  private loadAuthOptions(): void {
    this.authLoading.set(true);
    this.authService.listAuthConfigs(false).subscribe({
      next: (res) => {
        this.authOptions.set(res.authConfigs ?? []);
        this.authLoading.set(false);
      },
      error: () => {
        // The picker degrades to "no auth" — the tool can still be saved.
        this.authOptions.set([]);
        this.authLoading.set(false);
      },
    });
  }
}
